import { useMemo, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useAppTheme } from '@/theme/ThemeContext'
import { useCRMStore } from '@/store/crm/crmStore'
import {
  crmQueryKeys,
  useChangeCrmTaskStatus,
  useCrmReference,
} from '@/hooks/useCrm'
import {
  APICrmTask,
  CrmFilterResource,
  CrmSection,
  listCrmTasks,
} from '@/services/crmService'
import {
  CRM_SECTION_COLORS,
  CRM_SECTION_TITLES,
  CRM_SECTIONS,
  formatCrmDate,
  getPriorityIcon,
} from '@/utils/crm'
import { statusForSection } from '@/utils/crmTask'

/** One request per column; a column shows the latest tasks and says how many more exist. */
const COLUMN_SIZE = 50

const BoardCard = ({
  task,
  moving,
  onDragStart,
}: {
  task: APICrmTask
  moving: boolean
  onDragStart: () => void
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { setCRMTaskCreatorModal } = useCRMStore()
  const icon = getPriorityIcon(task.priority)
  const muted = isDarkMode ? '#9CA3AF' : '#8F98A5'

  return (
    <Box
      draggable
      role="button"
      tabIndex={0}
      onDragStart={e => {
        e.dataTransfer.setData('text/plain', String(task.id))
        e.dataTransfer.effectAllowed = 'move'
        onDragStart()
      }}
      onClick={() =>
        setCRMTaskCreatorModal({
          mode: 'edit',
          open: true,
          data: { taskId: task.id },
        })
      }
      onKeyDown={e => {
        if (e.key === 'Enter')
          setCRMTaskCreatorModal({
            mode: 'edit',
            open: true,
            data: { taskId: task.id },
          })
      }}
      sx={{
        cursor: 'grab',
        opacity: moving ? 0.5 : 1,
        backgroundColor: isDarkMode ? '#091327' : '#FFFFFF',
        border: `1px solid ${isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'}`,
        borderRadius: '6px',
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        fontFamily: 'Rubik, sans-serif',
        fontSize: '14px',
        color: isDarkMode ? '#FFFFFF' : '#003F61',
        '&:hover': { backgroundColor: isDarkMode ? '#2d4a7c' : '#E3F5FF' },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ fontWeight: 500 }}>{task.typeName || '-'}</Box>
        {icon && <img src={icon} alt="" width={14} height={14} />}
      </Box>
      <Box>{task.meterNumber || '-'}</Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ color: muted }}>{task.assigneeName || '-'}</Box>
        <Box
          sx={{
            color: task.overdue ? '#FE595A' : muted,
          }}
        >
          {formatCrmDate(task.dueAt)}
        </Box>
      </Box>
    </Box>
  )
}

const BoardColumn = ({
  section,
  accountId,
  filters,
  loading,
  movingId,
  dragging,
  onCardDragStart,
  onDropTask,
}: {
  section: CrmSection
  accountId: string
  filters: CrmFilterResource[]
  loading: boolean
  movingId: number | null
  dragging: boolean
  onCardDragStart: (task: APICrmTask) => void
  onDropTask: (section: CrmSection) => void
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [over, setOver] = useState(false)
  const apiResources = useMemo<CrmFilterResource[]>(
    () => [
      { name: 'section', value: [section], operator: 'EQUALS' },
      ...filters,
    ],
    [section, filters]
  )
  const { data, isFetching, isError } = useQuery({
    queryKey: [...crmQueryKeys.tasks(accountId), 'board', apiResources],
    queryFn: ({ signal }) =>
      listCrmTasks(
        accountId,
        { apiResources },
        { page: 1, size: COLUMN_SIZE, sortBy: 'updatedAt', sortOrder: 'DESC' },
        signal
      ),
    placeholderData: keepPreviousData,
  })
  const tasks = data?.result ?? []
  const color = CRM_SECTION_COLORS[section]

  return (
    <Box
      data-testid={`crm-board-${section}`}
      onDragOver={e => {
        if (!dragging) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        setOver(true)
      }}
      onDragLeave={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null))
          setOver(false)
      }}
      onDrop={e => {
        e.preventDefault()
        setOver(false)
        onDropTask(section)
      }}
      sx={{
        flex: 1,
        minWidth: 240,
        backgroundColor: isDarkMode ? '#202B40' : '#FAFBFD',
        border: `1px ${over ? 'dashed' : 'solid'} ${over ? color : isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'}`,
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        minHeight: 240,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Box
          sx={{
            background: color,
            color: '#EFF2F7',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '16px',
            borderRadius: '4.5px',
            padding: '4px 14px',
          }}
        >
          {CRM_SECTION_TITLES[section]}
        </Box>
        <Typography
          sx={{
            color: isError ? '#FE595A' : '#8F98A5',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
          }}
        >
          {isError ? 'שגיאה בטעינה' : (data?.total ?? '')}
        </Typography>
        {(isFetching || loading) && <CircularProgress size={14} />}
      </Box>
      {tasks.map(task => (
        <BoardCard
          key={task.id}
          task={task}
          moving={movingId === task.id}
          onDragStart={() => onCardDragStart(task)}
        />
      ))}
      {!isFetching && !isError && tasks.length === 0 && (
        <Typography
          sx={{
            color: '#8F98A5',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            textAlign: 'center',
            pt: '24px',
          }}
        >
          אין משימות
        </Typography>
      )}
      {data && data.total > tasks.length && (
        <Typography
          sx={{
            color: '#8F98A5',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '12px',
            textAlign: 'center',
          }}
        >
          {`מוצגות ${tasks.length} מתוך ${data.total}`}
        </Typography>
      )}
    </Box>
  )
}

/** The CRM tasks as three columns, one per section. Dropping a card moves its task to that section. */
const CRMBoard = ({
  accountId,
  filters = [],
  searching = false,
}: {
  accountId: string
  /** Filters applied on top of each column's `section` filter. */
  filters?: CrmFilterResource[]
  searching?: boolean
}) => {
  const { data: reference } = useCrmReference(accountId)
  const changeStatus = useChangeCrmTaskStatus(accountId)
  const [dragged, setDragged] = useState<APICrmTask | null>(null)
  const [movingId, setMovingId] = useState<number | null>(null)

  const handleDrop = async (section: CrmSection) => {
    const task = dragged
    setDragged(null)
    if (!task || task.section === section) return
    const statusId = statusForSection(reference, section)
    if (statusId == null) return
    setMovingId(task.id)
    try {
      await changeStatus(task.id, statusId)
    } finally {
      setMovingId(null)
    }
  }

  return (
    <Box
      dir="rtl"
      onDragEnd={() => setDragged(null)}
      sx={{
        display: 'flex',
        gap: '16px',
        marginTop: '30px',
        alignItems: 'stretch',
        overflowX: 'auto',
      }}
    >
      {CRM_SECTIONS.map(section => (
        <BoardColumn
          key={section}
          section={section}
          accountId={accountId}
          filters={filters}
          loading={searching}
          movingId={movingId}
          dragging={dragged != null}
          onCardDragStart={setDragged}
          onDropTask={handleDrop}
        />
      ))}
    </Box>
  )
}

export default CRMBoard
